import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  Switch,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
  Divider
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { createWebsiteImportJob, updateWebsiteImportJob } from '../services/api';

const CreateImportJobDialog = ({ open, onClose, onJobCreated, projectId, existingJob = null }) => {
  const [url, setUrl] = useState('');
  const [fetchWholeDomain, setFetchWholeDomain] = useState(false);
  const [limit, setLimit] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [scrapeFilter, setScrapeFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existingJob) {
      setUrl(existingJob.url);
      setFetchWholeDomain(existingJob.fetch_whole_domain);
      setLimit(existingJob.limit?.toString() || '');
      setPathFilter(existingJob.path_filter || '');
      setScrapeFilter(existingJob.scrape_filter || '');
    } else {
      setUrl('');
      setFetchWholeDomain(false);
      setLimit('');
      setPathFilter('');
      setScrapeFilter('');
    }
  }, [existingJob, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const jobData = {
        project_id: projectId,
        url,
        fetch_whole_domain: fetchWholeDomain,
        limit: limit ? parseInt(limit) : null,
        path_filter: pathFilter || null,
        scrape_filter: scrapeFilter || null
      };

      let newJob;
      if (existingJob) {
        newJob = await updateWebsiteImportJob(existingJob.id, jobData);
      } else {
        newJob = await createWebsiteImportJob(jobData);
      }
      
      onJobCreated(newJob);
      handleClose();
    } catch (error) {
      console.error('Error saving import job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUrl('');
    setFetchWholeDomain(false);
    setLimit('');
    setPathFilter('');
    setScrapeFilter('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {existingJob ? 'Edit Website Import Job' : 'Create Website Import Job'}
          {projectId && <Typography variant="subtitle2" color="textSecondary">
            In current project
          </Typography>}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Website URL"
              fullWidth
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              autoFocus
              placeholder="https://example.com/blog"
              type="url"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={fetchWholeDomain}
                  onChange={(e) => setFetchWholeDomain(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>Fetch whole domain</span>
                  <Tooltip title="When enabled, will crawl all pages under the same domain">
                    <IconButton size="small">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />

            <Divider />
            
            <TextField
              label="Scrape Filter (Regex)"
              fullWidth
              value={scrapeFilter}
              onChange={(e) => setScrapeFilter(e.target.value)}
              placeholder="^/blog/.*"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Regular expression to filter which URLs to scrape. URLs that don't match will be completely ignored. Example: ^/blog/.* will only scrape URLs starting with /blog/">
                      <IconButton size="small">
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Path Filter (Regex)"
              fullWidth
              value={pathFilter}
              onChange={(e) => setPathFilter(e.target.value)}
              placeholder="^/blog/.*"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Regular expression to filter which URLs to store content from. URLs that don't match will be scraped but their content won't be stored. Example: ^/blog/.* will only store content from URLs starting with /blog/">
                      <IconButton size="small">
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Page Limit"
              fullWidth
              value={limit}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d+$/.test(val)) {
                  setLimit(val);
                }
              }}
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Maximum number of pages to import. Leave empty for no limit">
                      <IconButton size="small">
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              placeholder="Optional"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !url}
          >
            {existingJob ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateImportJobDialog; 